import { Input } from '@/components/ui/input'
import { useCheckExistPhoneMutation } from '@/hooks/react-query/account/info/use-check-exist-phone.mutation'
import { useConfirmInfoUserMutation } from '@/hooks/react-query/auth/use-confirm-info-user.mutation'
import { validatePhone } from '@/lib/utils'
import { hidePopup, showPopup } from '@/providers/popup-provider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { toast } from '../common/toast'
import { ImageCM } from '../extension/image-cm'
import { Button } from '../ui/button'
import { OtpForm } from './otp-form'

type Props = {
  isConfirm?: boolean
  user: {
    fullname: string
    email: string
    id: string
  }
  onSuccess?: () => void
}

export const VerifyPhone = ({ user, isConfirm, onSuccess }: Props) => {
  const t = useTranslations('login')
  const { mutateAsync: checkExistPhone, isPending: isChecking } =
    useCheckExistPhoneMutation()

  const { mutateAsync: confirmInfoUser, isPending: isConfirming } =
    useConfirmInfoUserMutation()

  const formSchema = z.object({
    fullname: z.string().min(1, t('required_fullname')),
    phone: z.string().refine(
      (value) => {
        const phoneValidate = validatePhone(value)
        return phoneValidate.valid
      },
      {
        message: t('invalid_phone'),
      }
    ),
    email: z.string().email(t('invalid_email')),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullname: user.fullname,
      phone: '',
      email: user.email,
    },
  })

  const onPreSubmit = async (values: z.infer<typeof formSchema>) => {
    if (values.phone !== '') {
      const res = await checkExistPhone(values.phone)
      if (res?.data?.isExist) {
        form.setError('phone', {
          message: t('phone_already_exists'),
        })
        return
      }
    }

    await onSubmit(values)
  }

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      const res = await confirmInfoUser({ ...data, user_id: user.id })

      showPopup(
        <OtpForm
          onClose={() => hidePopup()}
          type='confirm-info'
          phone={form.watch('phone')}
          onSuccess={onSuccess}
          bodyAuth={{
            fullname: res.data.fullname,
            email: res.data.email,
            user_id: res.data.user_id,
          }}
          isAuth
        />
      )

      // Remove router.refresh() to prevent popup from closing immediately
      // router.refresh()
    } catch (error) {
      if (error instanceof Error) {
        toast.message({
          title: t('fail_title'),
          message: error.message,
        })
      }
    }
  }

  return (
    <div>
      <ImageCM src={'/March2024/mceclip4_81.jpg'} width={169} height={28} />
      <h2 className='mb-4.5 mt-5 font-criteria text-4xl font-medium leading-10 text-neutral-900'>
        {isConfirm ? t('confirm_info_title') : t('update_account_title')}
      </h2>
      <p className='font-sans text-base leading-5 text-neutral-900/70'>
        {isConfirm ? t('confirm_info_message') : t('update_account_message')}
      </p>

      <form onSubmit={form.handleSubmit(onPreSubmit)}>
        <div className='mt-6 space-y-4'>
          <div className='grid grid-cols-2 gap-4'>
            <Input
              {...form.register('fullname')}
              placeholder={t('fullname')}
              disabled={!!user.fullname}
              className='disabled:text-neutral-900/50'
            />
            <Input
              {...form.register('phone')}
              placeholder={t('phone')}
              className='disabled:text-neutral-900/50'
              error={{
                message: form.formState.errors.phone?.message,
              }}
            />
          </div>
          <Input
            {...form.register('email')}
            placeholder={t('email')}
            disabled={!!user.email}
            className='disabled:text-neutral-900/50'
          />
        </div>
        <Button
          type='submit'
          className='mt-6 w-full'
          variant={'black'}
          size={'large'}
          loading={isChecking || isConfirming}
          disabled={!form.formState.isValid}
        >
          {t('update')}
        </Button>
      </form>
    </div>
  )
}
